import classNames from 'classnames';
import { IconButton } from 'components/Buttons/IconButton';
import {
  deleteChatMessage,
  ReactionState,
  setSelectedChatUserId,
  MessageState
} from 'features/chat/chatSlice';
import { useCurrentUser, useIsBroadcaster } from 'features/current-user/hooks';
import { motion, useReducedMotion } from 'framer-motion';
import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useContext,
  useRef,
  useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'rootReducer';
import styles from './styles.module.scss';
import buttonStyles from 'components/Buttons/styles.module.scss';
import { DateTime } from 'luxon';
import { Icon } from 'components/Icons';
import { UserImage } from 'components/UserImage';
import fallbackImage from 'images/no-picture.svg';
import { UserAgentContext } from 'features/user-agent/userAgent';
import sanitizeHtml from 'sanitize-html';
import linkifyHtml from 'linkify-html';
import { santizeConfig } from 'lib/sanitize';
import { CommentReactions } from './CommentReactions';
import { CommentReactionPicker } from './CommentReactions/CommentReactionPicker';
import { useHover } from 'usehooks-ts';
import { useOnClickOutside } from 'usehooks-ts';
import { useCurrentEvent } from 'features/events/eventHooks';
import {
  pinCommentForEvent,
  unpinCommentForEvent,
  updateEvent
} from 'features/events/eventsSlice';

interface ChatCommentProps {
  id: string;
  comment: string;
  user: string;
  userId: string;
  date: DateTime;
  img: string;
  type: string;
  heartScale: number;
  lastElement?: boolean;
  reactions: ReactionState[];
  pinned?: boolean;
  onEdit?: () => void;
}

const ChatComment: ForwardRefRenderFunction<HTMLLIElement, ChatCommentProps> = (
  props,
  ref
) => {
  const dispatch = useDispatch();
  const socketId = useSelector((state: RootState) => state.hub.socketId);
  const isBroadcaster = useIsBroadcaster();
  const currentUser = useCurrentUser();
  const currentEvent = useCurrentEvent();
  const canPerformCommentActions =
    isBroadcaster || currentUser?.id === props.userId;
  const shouldReduceMotion = useReducedMotion();
  const channelId = useSelector(
    (state: RootState) => state.channel.channelDetails?.channelId
  );
  const channelOwnerId = useSelector(
    (state: RootState) => state.channel.channelDetails?.ownerId
  );
  const { isNativeAppWebview } = useContext(UserAgentContext);
  const commentRef = useRef<HTMLDivElement>(null);
  const commentIsHovered = useHover(commentRef);
  const [pickerOpen, setPickerOpen] = useState<boolean>(false);
  const pickerOpened = useCallback((opened: boolean) => {
    setPickerOpen(opened);
  }, []);
  const [commentActionsVisible, setCommentActionsVisible] =
    useState<boolean>(false);
  const commentActionsRef = useRef<HTMLDivElement>(null);
  const commentIsPinned =
    currentEvent && currentEvent.pinnedComment?.id === props.id;

  const showProfile = () => {
    dispatch(
      setSelectedChatUserId({
        id: props.userId,
        username: props.user,
        profileImageUrl: props.img
      })
    );
  };

  const handleCommentActionsClickOutside = () => {
    setCommentActionsVisible(false);
  };

  useOnClickOutside(commentActionsRef, handleCommentActionsClickOutside);

  const toggleCommentActions = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setCommentActionsVisible((prevState) => !prevState);
  };

  const pinMessage = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (channelId && currentEvent) {
      if (props.id == currentEvent.pinnedComment?.id) {
        if (window.confirm('Unpin this comment?')) {
          dispatch(unpinCommentForEvent(channelId, currentEvent.id));
          dispatch(updateEvent({ id: currentEvent.id, pinnedComment: null }));
        }
      } else {
        dispatch(pinCommentForEvent(channelId, currentEvent.id, props.id));
        const pinnedComment: MessageState = {
          id: props.id,
          content: props.comment,
          user_id: props.userId,
          display_name: props.user,
          created_at: props.date.toISO() ?? '',
          profile_image_url: props.img,
          type: props.type,
          heartScale: props.heartScale
        };
        dispatch(updateEvent({ id: currentEvent.id, pinnedComment }));
      }
    }
  };

  const deleteMessage = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (window.confirm('Delete this comment?')) {
      dispatch(deleteChatMessage(props.id, socketId));
      if (currentEvent && currentEvent.pinnedComment?.id === props.id) {
        dispatch(updateEvent({ id: currentEvent.id, pinnedComment: null }));
      }
    }
  };

  const linkifyOptions = {
    target: '_blank',
    rel: 'noopener',
    format: {
      url: (value: string) =>
        value.length > 50 ? value.slice(0, 50) + '…' : value
    },
    className: 'comment__url',
    ignoreTags: ['img']
  };

  const santizeComment = (comment: string) => {
    const cleanComment = sanitizeHtml(comment, santizeConfig());

    return (
      <p
        className={styles['comment__text__block']}
        dangerouslySetInnerHTML={{
          __html: linkifyHtml(cleanComment, linkifyOptions)
        }}
      ></p>
    );
  };

  if (props.type === 'heart') {
    return (
      <motion.li
        key={'heart'}
        initial={isNativeAppWebview ? false : { opacity: 0, y: 5 }}
        animate={isNativeAppWebview ? false : { opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 5 }}
        transition={{ duration: shouldReduceMotion ? 0 : 0.1 }}
        className={styles['comment-wrapper--heart']}
      >
        <div className={styles['comment--heart']}>
          <motion.div
            className={styles['comment--heart__icon']}
            style={{
              scale: 0.4
            }}
            animate={{ scale: props.heartScale }}
          >
            <Icon
              hidden={true}
              icon={'heart'}
              label={''}
              height={24}
              width={24}
            />
          </motion.div>
          <span>{props.comment}</span>
        </div>
      </motion.li>
    );
  }

  const addDefaultSrc = (event: any): void => {
    if (!event.target.src.endsWith(fallbackImage)) {
      event.target.src = fallbackImage;
    }
  };

  return (
    <motion.li
      key={'comment'}
      ref={props.lastElement ? ref : null}
      initial={isNativeAppWebview ? false : { opacity: 0, y: 5 }}
      animate={isNativeAppWebview ? false : { opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 5 }}
      transition={{ duration: shouldReduceMotion ? 0 : 0.1 }}
      className={styles['comment-wrapper']}
    >
      <div
        ref={commentRef}
        className={classNames(styles['comment'], {
          [styles['comment--broadcaster']]: channelOwnerId == props.userId,
          'picker-open': pickerOpen,
          [styles['comment--pinned']]: commentIsPinned
        })}
      >
        <div className={styles['comment__profile']}>
          <UserImage>
            <img
              src={props.img}
              alt={`Profile image for ${props.user}`}
              onError={addDefaultSrc}
              onClick={showProfile}
            />
          </UserImage>
        </div>
        <div className={styles['comment__content']}>
          <div className={styles['comment__meta']}>
            <button
              className={classNames(
                buttonStyles['button--link'],
                styles['comment__username']
              )}
              onClick={showProfile}
            >
              {props.user}
            </button>
            {!props.pinned ? (
              <span className={classNames('tnum', styles['comment__time'])}>
                {props.date.toRelative({ style: 'short' })}
              </span>
            ) : (
              <span
                className={classNames(
                  'tw-text-right tw-flex tw-gap-2 tw-text-white [.light-contrast_&]:tw-text-black tw-opacity-[0.6] tw-items-center',
                  styles['comment__time']
                )}
              >
                <Icon
                  icon={'pinFilled'}
                  height={12}
                  hidden={true}
                  label={'Pinned'}
                  width={12}
                />
                Pinned
              </span>
            )}
          </div>
          <div className="tw-flex tw-relative">
            <div className="tw-flex-1 [&_img]:tw-max-w-1/2 [&_img]:tw-rounded">
              {santizeComment(props.comment)}
            </div>
            {props.reactions.length === 0 && !props.pinned && (
              <div className="tw-mt-1 [&_.icon-button]:tw-text-xl [&_.icon-button][.ios_&]:tw-text-sm [&_svg]:tw-h-[16px] [&_svg]:tw-w-[16px]">
                <CommentReactionPicker
                  commentId={props.id}
                  parentIsHovered={commentIsHovered}
                  pickerOpened={pickerOpened}
                />
              </div>
            )}
            {canPerformCommentActions && (
              <div
                ref={commentActionsRef}
                className={`tw-relative -tw-mr-[12px] -tw-mt-[6px] ${
                  props.pinned
                    ? '[&_*]:!tw-text-white [.light-contrast_&_*]:!tw-text-black'
                    : '[&_*]:!tw-text-label'
                }`}
              >
                <IconButton
                  icon={'ellipsisVertical'}
                  label={'Comment actions'}
                  size={'small'}
                  showLabel={false}
                  noBackground={true}
                  onClick={(event) => toggleCommentActions(event)}
                />
                <div
                  className={`${
                    props.pinned ? 'tw-bg-theme-color' : 'tw-bg-background'
                  } tw-absolute tw-right-[12px] -tw-top-[2px] tw-rounded-full tw-shadow ${
                    commentActionsVisible ? 'tw-block' : 'tw-hidden'
                  }`}
                >
                  <ul className="tw-list-none tw-m-0 tw-p-0 tw-flex tw-items-center tw-gap-0 tw-px-2">
                    {props.pinned &&
                      isBroadcaster &&
                      props.onEdit &&
                      channelOwnerId == props.userId && (
                        <li>
                          <IconButton
                            icon={'edit'}
                            label={'Edit'}
                            size={'small'}
                            showLabel={false}
                            noBackground={true}
                            onClick={() => props.onEdit?.()}
                          />
                        </li>
                      )}
                    {isBroadcaster && (
                      <li>
                        <IconButton
                          icon={commentIsPinned ? 'pinFilled' : 'pinOutline'}
                          label={
                            commentIsPinned ? 'Unpin Comment' : 'Pin Comment'
                          }
                          size={'small'}
                          showLabel={false}
                          noBackground={true}
                          onClick={(event) => pinMessage(event)}
                        />
                      </li>
                    )}
                    <li>
                      <IconButton
                        icon={'trash'}
                        label={'Delete'}
                        size={'small'}
                        showLabel={false}
                        noBackground={true}
                        onClick={(event) => deleteMessage(event)}
                      />
                    </li>
                    <li>
                      <IconButton
                        icon={'x'}
                        label={'Close comment actions'}
                        size={'mini'}
                        showLabel={false}
                        noBackground={true}
                        onClick={(event) => toggleCommentActions(event)}
                      />
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {!props.pinned && (
        <CommentReactions commentId={props.id} reactions={props.reactions} />
      )}
    </motion.li>
  );
};

export default forwardRef<HTMLLIElement, ChatCommentProps>(ChatComment);
